import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaChevronDown, FaChevronUp, FaCar, FaMoneyBillWave, FaUserCog, FaFileInvoiceDollar } from 'react-icons/fa';
import { ENDPOINT } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import moment from 'moment';
import './CarwashReport.css';

// Reusable Collapsible Section Component
const CollapsibleSection = ({ title, data, renderItem, count }) => {
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className="section-container">
      <div className="section-header" onClick={() => setCollapsed(!collapsed)}>
        <h3 className="section-title">{`${title} (${count})`}</h3>
        {collapsed ? <FaChevronDown /> : <FaChevronUp />}
      </div>
      {!collapsed && (
        <div className="section-content">
          {data.length > 0 ? data.map(renderItem) : <p className="no-data">No data for this period.</p>}
        </div>
      )}
    </div>
  );
};

const CarwashReport = () => {
  const { user } = useAuth();
  const [fromDate, setFromDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [details, setDetails] = useState(null);

  const fetchReport = async () => {
    setLoading(true);
    setReport(null);
    setDetails(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        ENDPOINT,
        {},
        {
          params: {
            action: 'get_carwash_report',
            from_date: fromDate,
            to_date: toDate,
          },
          headers: { 
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }) 
          },
        }
      );

      if (response.data.success) {
        setReport(response.data.report);
        setDetails(response.data.details);
      } else {
        toast.error(response.data.message || 'Failed to fetch report');
      }
    } catch (error) {
      console.error('Report fetch error:', error);
      toast.error('Failed to fetch report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ' ብር';
  };

  return (
    <div className="carwash-report">
      <div className="report-card">
        <h2 className="report-title">Carwash Analytics Report</h2>
        
        <div className="date-filters">
          <div className="form-group">
            <label htmlFor="fromDate">From:</label>
            <input
              type="date"
              id="fromDate"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="form-control"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="toDate">To:</label>
            <input
              type="date"
              id="toDate"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="form-control"
              max={moment().format('YYYY-MM-DD')}
            />
          </div>
          
          <button 
            className="fetch-button"
            onClick={fetchReport}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Fetch Report'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Generating report...</p>
        </div>
      )}

      {report && (
        <div className="report-card metrics">
          <h3 className="metrics-title">Summary</h3>
          
          <div className="metrics-grid">
            <div className="metric-item">
              <FaCar className="metric-icon" />
              <div>
                <p className="metric-label">Total Wash</p>
                <p className="metric-value">{formatCurrency(report.total_wash)}</p>
              </div>
            </div>
            
            <div className="metric-item">
              <FaUserCog className="metric-icon" />
              <div>
                <p className="metric-label">Workers' Share</p>
                <p className="metric-value">{formatCurrency(report.workers_percent)}</p>
              </div>
            </div>
            
            <div className="metric-item">
              <FaMoneyBillWave className="metric-icon" />
              <div>
                <p className="metric-label">Expenses</p>
                <p className="metric-value">{formatCurrency(report.expense)}</p>
              </div>
            </div>
            
            <div className="metric-item">
              <FaFileInvoiceDollar className="metric-icon" />
              <div>
                <p className="metric-label">Net Income</p>
                <p className="metric-value">{formatCurrency(report.net_income)}</p>
              </div>
            </div>
            
            <div className="metric-item">
              <FaMoneyBillWave className="metric-icon" />
              <div>
                <p className="metric-label">Tenth (10%)</p>
                <p className="metric-value">{formatCurrency(report.net_income * 0.1)}</p>
              </div>
            </div>
            
            <div className="metric-item">
              <FaMoneyBillWave className="metric-icon" />
              <div>
                <p className="metric-label">Institutional Cost</p>
                <p className="metric-value">{formatCurrency(report.institutional_cost)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {details && (
        <div className="details-container">
          <CollapsibleSection
            title="Carwash Transactions"
            count={details.carwash_transactions?.length || 0}
            data={details.carwash_transactions || []}
            renderItem={(item, index) => (
              <div key={`wash-${index}`} className="list-item">
                <div className="list-item-content">
                  <p className="list-item-main">
                    {`${item.transaction_date?.slice(0, 10) || item.transaction_date} - Plate: ${item.plate_number || 'N/A'}`}
                  </p>
                  <p className="list-item-sub">
                    {`Washer: ${item.worker_name || item.worker_id || 'N/A'}`}
                  </p>
                </div>
                <p className="list-item-amount">
                  {`Tariff: ${formatCurrency(item.tariff)}`}
                </p>
              </div>
            )}
          />

          <CollapsibleSection
            title="Car Spendings"
            count={details.car_spendings?.length || 0}
            data={details.car_spendings || []}
            renderItem={(item, index) => (
              <div key={`spending-${index}`} className="list-item">
                <div className="list-item-content">
                  <p className="list-item-main">
                    {`${item.transaction_date?.slice(0, 10) || item.transaction_date} - ${item.reason || 'No reason provided'}`}
                  </p>
                  <p className="list-item-sub">
                    {`Category: ${item.category || 'Uncategorized'}`}
                  </p>
                </div>
                <p className="list-item-amount">
                  {formatCurrency(item.amount)}
                </p>
              </div>
            )}
          />

          <CollapsibleSection
            title="Worker Credits"
            count={details.workers_credit?.length || 0}
            data={details.workers_credit || []}
            renderItem={(item, index) => (
              <div key={`credit-${index}`} className="list-item">
                <div className="list-item-content">
                  <p className="list-item-main">
                    {`${item.created_at?.slice(0, 10) || item.created_at} - Worker: ${item.worker_name || item.worker_id || 'N/A'}`}
                  </p>
                  <p className="list-item-sub">
                    {item.reason || 'No reason provided'}
                  </p>
                </div>
                <p className="list-item-amount">
                  {formatCurrency(item.amount)}
                </p>
              </div>
            )}
          />

          <CollapsibleSection
            title="Paid Worker Credits"
            count={details.paid_workers_credit?.length || 0}
            data={details.paid_workers_credit || []}
            renderItem={(item, index) => (
              <div key={`paid-credit-${index}`} className="list-item">
                <div className="list-item-content">
                  <p className="list-item-main">
                    {`${item.created_at?.slice(0, 10) || item.created_at} - Worker: ${item.worker_name || item.worker_id || 'N/A'}`}
                  </p>
                  <p className="list-item-sub">
                    {item.reason || 'No reason provided'}
                  </p>
                </div>
                <p className="list-item-amount">
                  {formatCurrency(item.amount)}
                </p>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default CarwashReport;
